import React, { useEffect, useState } from 'react';
import { apiGet, apiPut, apiPost, apiDel } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Plus, X, ArrowRight, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  Induction: 'bg-violet-100 text-violet-700',
  KYC: 'bg-teal-100 text-teal-700',
  IT: 'bg-emerald-100 text-emerald-700',
  Asset: 'bg-amber-100 text-amber-700',
  Training: 'bg-red-100 text-red-700',
  Legal: 'bg-slate-200 text-slate-700',
  Benefits: 'bg-pink-100 text-pink-700',
  People: 'bg-teal-100 text-teal-700',
  Review: 'bg-orange-100 text-orange-700',
};

const OWNER_COLORS = {
  HR: 'bg-violet-100 text-violet-700 border-violet-200',
  IT: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Admin: 'bg-amber-100 text-amber-700 border-amber-200',
  Manager: 'bg-teal-100 text-teal-700 border-teal-200',
};

function isOverdue(t) {
  if (t.is_done || !t.due_date) return false;
  return new Date(t.due_date) < new Date(new Date().toDateString());
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [board, setBoard] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTask, setNewTask] = useState({ label: '', category: 'Induction', owner_role: 'HR', due_date: '' });
  const [validationBlock, setValidationBlock] = useState(null); // {message, redirect_to, task_label}

  const loadEmployees = async () => {
    try {
      const all = await apiGet('/hr/employees');
      // Change 19 — Consultants have no onboarding checklist, exclude them
      // from the worklist entirely.
      const list = (all || []).filter(e => (e.employment_type || 'On-Roll') !== 'Consultant');
      setEmployees(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (err) { console.error(err); }
  };

  const loadBoard = async (empId) => {
    if (!empId) return;
    try {
      const res = await apiGet(`/hr/onboarding/${empId}`);
      setBoard(res);
    } catch (err) { toast.error('Failed to load onboarding'); }
  };

  useEffect(() => { loadEmployees(); }, []);
  useEffect(() => { loadBoard(selectedId); }, [selectedId]);

  const toggleTask = async (taskId) => {
    try {
      await apiPut(`/hr/onboarding/tasks/${taskId}/toggle`);
      loadBoard(selectedId);
    } catch (err) {
      // Phase B — 409 = validation guard. Surface the message + redirect option.
      const status = err?.response?.status || err?.status;
      const detail = err?.response?.data?.detail || err?.detail || err?.data?.detail;
      if (status === 409 && detail && typeof detail === 'object') {
        setValidationBlock({
          ...detail,
          task_id: taskId,
        });
        toast.error(detail.message || 'Task cannot be completed yet');
      } else {
        toast.error('Failed to toggle');
      }
    }
  };

  const goToFix = () => {
    if (!validationBlock?.redirect_to) return;
    const url = validationBlock.redirect_to.replace('{employee_id}', selectedId);
    setValidationBlock(null);
    navigate(url);
  };

  // Clicking a task name takes the user to where the underlying artifact is produced
  // (document tasks → Document Vault; ID/field tasks → Employee edit; letters → Letters).
  const openTaskTarget = (t) => {
    const target = t?.validator?.redirect_to;
    if (!target) return;
    navigate(target.replace('{employee_id}', selectedId));
  };

  const addTask = async (e) => {
    e.preventDefault();
    try {
      await apiPost(`/hr/onboarding/${selectedId}/tasks`, { ...newTask, is_done: false });
      toast.success('Task added');
      setShowAdd(false);
      setNewTask({ label: '', category: 'Induction', owner_role: 'HR', due_date: '' });
      loadBoard(selectedId);
    } catch (err) { toast.error('Failed to add task'); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this onboarding task?\n\nThis action cannot be undone.')) return;
    try {
      await apiDel(`/hr/onboarding/tasks/${taskId}`);
      loadBoard(selectedId);
    } catch (err) { toast.error('Failed to delete'); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Onboarding</h1>
          <p className="text-sm text-slate-600 mt-1">Track every new joiner's induction, KYC, IT setup, asset assignment and policy acknowledgement.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Employees list */}
        <aside className="bg-white border border-slate-200 rounded-sm">
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">Employees</div>
          </div>
          <ul className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {employees.map(emp => (
              <li key={emp.id}>
                <button
                  onClick={() => setSelectedId(emp.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${selectedId === emp.id ? 'bg-teal-50/60 border-l-2 border-l-[#1F7A6E]' : ''}`}
                  data-testid={`onb-emp-${emp.id}`}
                >
                  <div className="font-display font-semibold text-sm text-[#0F172A] truncate">{emp.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{emp.emp_id} · {emp.designation || '—'}</div>
                </button>
              </li>
            ))}
            {!employees.length && <li className="p-4 text-center text-sm text-slate-500">No employees</li>}
          </ul>
        </aside>

        {/* Board */}
        <main className="space-y-4">
          {board ? (
            <>
              {/* Progress */}
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">Progress</div>
                    <div className="font-display text-2xl font-extrabold text-[#0F172A] mt-1">{board.employee_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl font-extrabold text-[#1F7A6E]">{board.progress_pct}%</div>
                    <div className="text-xs text-slate-500">{board.done} of {board.total} complete</div>
                  </div>
                </div>
                <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
                  <div className="h-full bg-[#1F7A6E] transition-all" style={{ width: `${board.progress_pct}%` }} />
                </div>
              </div>

              {/* By-Owner Mini Panel */}
              {board.by_owner && Object.keys(board.by_owner).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Tasks by Owner</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(board.by_owner).map(([owner, stats]) => {
                      const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;
                      const ocolor = OWNER_COLORS[owner] || 'bg-slate-100 text-slate-700 border-slate-200';
                      return (
                        <div key={owner} className={`px-3 py-2 rounded-sm border ${ocolor}`} data-testid={`owner-pill-${owner}`}>
                          <div className="text-[10px] uppercase tracking-wider font-display font-bold">{owner}</div>
                          <div className="text-xs mt-0.5">{stats.done} / {stats.total} · {pct}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tasks */}
              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
                  <h3 className="font-display font-bold text-lg">Checklist</h3>
                  <button onClick={() => setShowAdd(true)} className="text-xs font-display font-bold uppercase tracking-wider text-[#1F7A6E] hover:underline flex items-center gap-1" data-testid="add-onb-task-btn">
                    <Plus size={12} /> Add Task
                  </button>
                </div>
                <ul className="divide-y divide-slate-100">
                  {board.tasks.map(t => (
                    <li key={t.id} className={`px-5 py-3 flex items-center gap-3 hover:bg-slate-50 ${isOverdue(t) ? 'bg-red-50/50' : ''}`} data-testid={`onb-task-${t.id}`}>
                      <button onClick={() => toggleTask(t.id)} className={t.is_done ? 'text-emerald-600' : 'text-slate-400 hover:text-[#1F7A6E]'} data-testid={`toggle-task-${t.id}`}>
                        {t.is_done ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        {t.validator?.redirect_to ? (
                          <button
                            onClick={() => openTaskTarget(t)}
                            data-testid={`open-task-${t.id}`}
                            title="Open where this task is completed"
                            className={`group text-left font-display font-semibold text-sm inline-flex items-center gap-1.5 ${t.is_done ? 'line-through text-slate-500' : 'text-[#0F172A] hover:text-[#1F7A6E]'}`}
                          >
                            {t.label}
                            <ExternalLink size={12} className="shrink-0 opacity-40 group-hover:opacity-100 text-[#1F7A6E] transition-opacity" />
                          </button>
                        ) : (
                          <div className={`font-display font-semibold text-sm ${t.is_done ? 'line-through text-slate-500' : 'text-[#0F172A]'}`}>{t.label}</div>
                        )}
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                          {t.due_date && (
                            <span className={isOverdue(t) ? 'text-red-700 font-bold' : ''}>
                              Due: {new Date(t.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              {isOverdue(t) && ' · OVERDUE'}
                            </span>
                          )}
                          {t.done_at && <span>Done by {t.done_by} · {new Date(t.done_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      {t.owner_role && (
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm font-display font-bold ${OWNER_COLORS[t.owner_role] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>{t.owner_role}</span>
                      )}
                      {t.category && (
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${CATEGORY_COLORS[t.category] || 'bg-slate-100 text-slate-700'}`}>{t.category}</span>
                      )}
                      <button onClick={() => deleteTask(t.id)} className="text-slate-400 hover:text-red-600"><X size={14} /></button>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-sm text-slate-500">
              Select an employee on the left to view their onboarding board.
            </div>
          )}
        </main>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Add Onboarding Task</h2>
              <button onClick={() => setShowAdd(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={addTask} className="p-5 space-y-4">
              <div>
                <label className="label">Task *</label>
                <input type="text" value={newTask.label} onChange={(e) => setNewTask({ ...newTask, label: e.target.value })} className="input" placeholder="e.g., Photo upload completed" required />
              </div>
              <div>
                <label className="label">Category</label>
                <select value={newTask.category} onChange={(e) => setNewTask({ ...newTask, category: e.target.value })} className="input">
                  <option>Induction</option><option>KYC</option><option>IT</option><option>Asset</option><option>Training</option><option>Legal</option><option>Benefits</option><option>People</option><option>Review</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Owner</label>
                  <select value={newTask.owner_role} onChange={(e) => setNewTask({ ...newTask, owner_role: e.target.value })} className="input" data-testid="new-task-owner">
                    <option>HR</option><option>IT</option><option>Admin</option><option>Manager</option>
                  </select>
                </div>
                <div>
                  <label className="label">Due Date</label>
                  <input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} className="input" data-testid="new-task-due" />
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Add Task</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Phase B — Validator failure modal */}
      {validationBlock && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" data-testid="validation-block-modal">
          <div className="bg-white rounded-md max-w-md w-full shadow-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-amber-50">
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-600" />
                <h3 className="font-display font-bold">Task cannot be marked complete</h3>
              </div>
              <button onClick={() => setValidationBlock(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">
                <b>{validationBlock.task_label}</b>
              </p>
              <p className="text-sm text-slate-600">{validationBlock.message}</p>
              <p className="text-xs text-slate-400">
                Once the underlying artifact (letter / document / employee field) is created, this task will auto-complete.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setValidationBlock(null)} className="px-4 py-2 border border-slate-300 rounded-sm text-sm">Cancel</button>
              {validationBlock.redirect_to && (
                <button onClick={goToFix} className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-sm font-semibold flex items-center gap-1.5" data-testid="go-fix-btn">
                  Take me there <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
